import React, { useMemo, useCallback, useRef, useState, useEffect, useContext } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { useDrop, useDrag, DragPreviewImage } from 'react-dnd';
import { Tooltip, Icon, message, Checkbox, Popconfirm } from 'antd';
import {
  EditorWrapper, EditingContainer, PageMetaDetails, PageNumber, PageActions, PageActionItem,
  RulerContainer, RulerIcon, PageContent
} from './styles';

import BlockLayout from './BlockLayout';
import PageStaticElement from './PageStaticElement';
import VariableMenuContainer from './VariableMenuContainer';
import CustomDragLayer from '../SidebarMenu/CustomDragLayer';
import Ruler from '../../artboard/Page/Ruler';
import { BUSINESS_LOGIC_TAB, COMPLEX_PAGE_OPTIONS } from './translationValues';
import ReusableDocumentFlowPreview from '../../images/reusable_document_flow.png';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../context/complexArtboardContext';
import {
  HEADER, FOOTER, COMPLEX_BLOCK_TYPE, CREATIVE_BLOCK, DYNAMIC_TABLE_BLOCK, REPLACE_FLOW,
  PAGE_NUMBER, dummyCallback, REUSABLE_SECTION, DESIGN_TYPE, TABLE_OF_CONTENTS,
  STATIC_TABLE_BLOCK, HTML_BLOCK
} from '../../utils/complexDocumentConstants';
import createResizeObserver from './useResizeObserver';
import createIntersectionObserver from './useIntersectionObserver';
import { getElementBySelectorAsync } from './complexDocumentHelper';
import { COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../utils/complexDocumentConstants';
import { PagesWrapper } from './styles';
import styled from 'styled-components';
import BlockLayoutSettings from './BlockLayoutSettings';
import withScrolling from '../../components/ScrollZone';
import ComplexNewPage from './ComplexNewPage';
import { PageNumberRenderer } from './CreativeBlockDesign/PageNumber/pageNumberRender';
import { AddressWindow } from './CreativeBlockDesign/AddressWindow';
import { getWidthHeightScale, checkSessionUserAccess, getBusinessLogicByType } from '../../utils/helper';
import { DESIGN_AREA_TEMPLATES_TAB } from '../../containers/SidebarMenu/translationValues';
import { detectLanguageDynamic } from '../../utils/services/textGenerationAPI';
import { scrollPageTo } from '../../utils/javascript';
import { useSessionContext } from '../../context/sessionContext';
import { updateMasterTemplateToDocument } from '../../utils/masterTemplateHelper';
import CustomTooltip from '../../components/CustomTooltip/CustomTooltip';
import ComplexDefaultStyleProvider, { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { getTocFlow } from '../../utils/services/complexDocumentsAPI';
import { BusinessLogicSvg, ReuseFlowIcon, RulerSvg } from '../../images/svgIcons';
import { isLanguageTranslationDisabled } from '../../utils/detectLanguageHelper';
import { BusinessLogicOptionPopover } from '../../components/BusinessLogicModal';
import { BusinessLogic } from '../../context/businessLogicContext';
import { deleteBusinessLogic } from '../../utils/services/businessLogicApi';
import ContextMenuContainer from './contextMenuContainer';
import { INSERTS_TAB } from './ComplexPdfInserts/translationValues';
import { useShowVersionSettings } from './VersionSettings/useShowVersionSettings';

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS','DOCUMENT_DESIGN_SECTION'];

const ToolTipPopup = styled.div``;

const dropAcceptKeys =  [HEADER, FOOTER, REPLACE_FLOW, ...COMPLEX_BLOCK_TYPE];

const DesignPage = ({ uuid, pageId, flowNumber, scaling, rulerEnabled, width, height, backgroundColor, pageOverflowEnabled, isStandalone,
  widthInMM, headerUUID, footerUUID, useStore, setParentHeight, getElementStyles, pageNumberUUID ,editable, CurrentDesign, observer, isTocFlow, isDocToMail  }) => {

  const { t } = useTranslation(NAMESPACES);
  const [isPageVisible, setPageVisible] = useState(isDocToMail ? true : false);
  
  const customSelector = useCallback(() => null, []);
  const [, methods] = useDocumentContext(customSelector, `DesignPage-${uuid}`);

  const [rulerHeight, setRulerHeight] = useState(height);

  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === uuid && store[CONTEXT_KEYS.CURRENT_BLOCK] !== '' , []);
  const [blockEditing, updateArtboard, , , getLatestArtboard] = useArtboardContext(customArtboardSelector, `DesignPage-${uuid}`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, '');

  const textElementSelector = useCallback(store => store[CONTEXT_KEYS.TEXT_ELEMENT_REF], []);
  const [editingBlockOrElement] = useArtboardContext(textElementSelector, `DesignPage-${uuid}`);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);

  const [currentBlock] = useArtboardContext(artboardContextSelector);
    const pageNumberSelector = useCallback(store => {
    return store[pageNumberUUID]
  }, [pageNumberUUID]);

  const [pageNumber] = useDocumentContext(pageNumberSelector, `PageNumber-${pageNumberUUID}`);

  const dndFlowSelector = useCallback(store => store[CONTEXT_KEYS.DND_FLOW], []);
  const [isDndFlow] = useArtboardContext(dndFlowSelector, `DesignPage-${uuid}`);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const docPageNumberSelector = useCallback(
    state =>
      CurrentDesign === 'document'
        ? state[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES][PAGE_NUMBER]
        : null,
    [pageNumberUUID],
  );
  const [docPageNumber] = useDocumentContext(docPageNumberSelector);

  const onIntersectionCallback = useCallback((entry) => {
    if (entry.isIntersecting) {
      console.log("Intersecting ", pageId, " || ", entry);
      setPageVisible(existingValue => {
        if (!existingValue) {
          const scrollParent = entry.target.parentNode.parentNode;
          console.log({ scrollParent });
          const allPages = Array.from(scrollParent.children);
          const indexOfThisPage = allPages.findIndex(child => child === entry.target.parentNode);
          if (indexOfThisPage !== -1) {
            allPages.slice(0, indexOfThisPage).forEach(
              page => {
                if (page.id.startsWith('scroll-') && page.children[0]) {
                  page.children[0].dispatchEvent(new CustomEvent('alreadyScrolled'))
                }
              }
            )
          }
        }
        return true;
      });
    }
  }, []);

  const onAlreadyScrolledEvent = useCallback((customEvent) => {
    setPageVisible(true);
  }, []);

  useEffect(() => {
    if (!isPageVisible) {
      const editingContainerDOM = document.getElementById(`editingContainer-${pageId}`);
      if (editingContainerDOM !== undefined) {
        editingContainerDOM.addEventListener('alreadyScrolled', onAlreadyScrolledEvent);
        const removeObserver = observer.subscribeToIntersection(document.getElementById(`editingContainer-${pageId}`), onIntersectionCallback, `DesignPage-${pageId}`);
        return () => {
          editingContainerDOM.addEventListener('alreadyScrolled', onAlreadyScrolledEvent);
          removeObserver();
        }
      }
    }
  }, [isPageVisible]);

  const detectLanguage = (currentBlock) => {
    if (isLanguageTranslationDisabled(organisationDetails)) return;
    const currentStore = methods.get();
    const blockId = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][currentBlock];
    const templateLanguage = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE];
    const isLanguageDetected = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_LANGUAGE_DETECTED];
    if (!templateLanguage && !isLanguageDetected && blockId) {
      const payload = {
        id: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
        type: CurrentDesign,
        elementId: blockId,
        elementType: DESIGN_TYPE.BLOCK,
        isStandalone: false,
      };
      detectLanguageDynamic(payload)
        .then(response => {
          const statusCode = response?.statusCode;
          if (statusCode === 734 || statusCode === 742) {
            methods.set({
              [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_LANGUAGE_DETECTED]: true,
              [COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE]: response.data,
            });
          }
        })
        .catch(error => console.log({ languageDetectionError: error }));
    }
  };

  const disableEditing = useCallback(() => {
    // Introducing slight deley to delect language so auto save will be finished before detect language api is called
    const timeout = setTimeout(() => {
      detectLanguage(currentBlock);
      clearTimeout(timeout);
    }, 1000);
    updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false, [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false, [CONTEXT_KEYS.CURRENT_BLOCK]: '', [CONTEXT_KEYS.CURRENT_PAGE]: '' });
  }, [detectLanguage, currentBlock]);

  const pageRef = useRef(null);

  const onDrop = useCallback((item, monitor) => {
    if (monitor.didDrop()) return;

    if (item.type === REPLACE_FLOW) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: item.template.language,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: true,
      });
    } else {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.NEW_LANGUAGE_CODE]: item.element.language,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_DRAG_ACTION]: true,
      });
    };

    if (item.type === REPLACE_FLOW) {
      if (item.template) {
        const template = item.template;
        updateMasterTemplateToDocument(methods, template.id, item.element).then(({ flows }) => {
          if (flows?.length) {
            methods.replaceFlow({ flow: flows[0], uuid, pageId });
          } else {
            methods.replaceFlow({ flow: item.element, uuid, pageId });
          }
        });
      } else {
        methods.replaceFlow({ flow: item.element, uuid, pageId });
      }
      return;
    } else if (item.type === REUSABLE_SECTION) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: {
          blockId: item.id,
          blocks: item.element.elementDetails,
          variableContract: item.element?.variableContract || [],
          style: item.element.style,
          uuid,
          pageId,
          dropType: 'over',
          dropItem: 'last',
          currentPageId: item.pageId,
          sectionType: 'content'
        },
      });

      if (checkSessionUserAccess(userDetails, 'master')) {
        updateArtboard({
          [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,     
        });
      }else{
        updateArtboard({
          [CONTEXT_KEYS.CONFIMATION_PAGE_MODAL]: true,
          [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,         
        });
      }
      
    } else if ([HEADER, FOOTER].includes(item.type)) {
      if ([HEADER, FOOTER].includes(item.sectionType)) {
        methods.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: {
            blockId: item.id,
            blocks: item.element.elementDetails,
            style: item.element?.style,
            uuid,
            type: item.type,
            sectionType: item.sectionType,
          },
        });

        if (checkSessionUserAccess(userDetails, 'master')) {
          updateArtboard({
            [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,     
          });
        }else{
          updateArtboard({
            [CONTEXT_KEYS.CONFIMATION_PAGE_MODAL]: true,
            [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,         
          });
        }
      } else {
        methods.setPage('addStaticElement', {
          uuid,
          type: item.type,
          block: Object.assign({}, item.element.elementDetails),
        });
      }
    } else if (item.type === STATIC_TABLE_BLOCK && !item.element.elementDetails?.uuid) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.GRID_LAYOUT_BLOCK]: {
          uuid,
          pageId,
          draggedItem:item,
          dropType: 'over',
          dropItem: 'last',
          currentPageId: item.pageId,
        }
      });
    }else {
      item.element.elementDetails.groups = [];     
      const newBlockUUID = methods.setPage('addBlock', {
        uuid,
        block: Object.assign({}, item.element.elementDetails),
        pageId,
        dropType: 'over',
        dropItem: 'last',
        currentPageId: item.pageId,
      });
      // TODO : Activate current block for editing
      if (newBlockUUID) {
        const artboardObj = {
          [CONTEXT_KEYS.CURRENT_BLOCK]: newBlockUUID,
          [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: item.element.elementDetails.type,
          [CONTEXT_KEYS.CURRENT_PAGE]: uuid,
          [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]:
            item.element.elementDetails.type === CREATIVE_BLOCK || item.element.elementDetails.type === STATIC_TABLE_BLOCK,
          [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]:
            item.element.elementDetails.type === DYNAMIC_TABLE_BLOCK,
        };
        if (item.element.type === HTML_BLOCK) {
          artboardObj[CONTEXT_KEYS.CURRENT_BLOCK] = '';
          artboardObj[CONTEXT_KEYS.CURRENT_BLOCK_TYPE] = '';
          artboardObj[CONTEXT_KEYS.CURRENT_PAGE] = '';
        }
        updateArtboard(artboardObj);
    }
    }
  }, []);

  const [isOver, drop] = useDrop({
    canDrop: () => editable,
    accept: isTocFlow ? [] : isDocumentOrFlowDesign ? [...dropAcceptKeys, REUSABLE_SECTION] : dropAcceptKeys,
    collect: monitor => !!monitor.isOver({ shallow: true }),
    drop: onDrop
  });

  useEffect(() => {
    if (editable) {
      drop(pageRef);
    }
  }, [editable]);

  const [, dropPageNumber] = useDrop({
    canDrop: () => editable,
    accept: [PAGE_NUMBER],
    collect: monitor => !!monitor.isOver({ shallow: true }),
    drop: (element, monitor) => {
      if (monitor.didDrop()) return;
      if (uuid) {
        let offset, cursorOffset;
        if (monitor.getClientOffset()) {
          offset = monitor.getSourceClientOffset();
          cursorOffset = monitor.getClientOffset();
        }
        const dropTarget = document.getElementById(uuid);
        let dropTargetXy = dropTarget.getBoundingClientRect();

        const tempEl = JSON.parse(JSON.stringify(element.element));
        const elementDetails = tempEl.elementDetails;
        const contents = elementDetails.contents;

        const elementWidth = Number(contents.style.width.replace(/px$/, ''));
        const elementHeight = Number(contents.style.height.replace(/px$/, ''));

        contents.style.width = Number(contents.style.width.replace(/px$/, ''));
        contents.style.height = Number(contents.style.height.replace(/px$/, ''));
        let extractedTransform = {};
        if (tempEl.elementDetails?.contents?.style?.transform) {
          const transformStyles = tempEl.elementDetails.contents.style.transform.split(' ');
          transformStyles.forEach(data => {
            let key = data.split('(')[0];
            let value = data && data.match(/\((.*)\)/)[1];
            value = Number(value.replace(/[^\d.-]/g, ''));
            let pair = { [key]: value };
            extractedTransform = { ...extractedTransform, ...pair };
          });
        }

        let xOffset = 0;
        let yOffset = 0;
        if (monitor && offset && tempEl) {
          xOffset = cursorOffset.x - offset.x;
          yOffset = cursorOffset.y - offset.y;
          const widthRatio = xOffset / element.boundaries.width;
          xOffset = elementWidth * widthRatio;
          const heightRatio = yOffset / element.boundaries.height;
          yOffset = elementHeight * heightRatio;

          contents.style.x = (cursorOffset.x - dropTargetXy.left) / scaling - xOffset;
          contents.style.y = (cursorOffset.y - dropTargetXy.top) / scaling - yOffset;
          contents.style.rotate = extractedTransform.rotate || 0;
        } else {
          contents.style.x = (dropTargetXy.width / scaling - contents.style.width) / 2;
          contents.style.y = (dropTargetXy.height / scaling - contents.style.height) / 2;
          contents.style.rotate = extractedTransform.rotate || 0;
        }
        elementDetails.contents = contents;
        methods.addPageNumber({
          uuid: uuid,
          block: {
            contents: contents,
            pageNumberType: elementDetails.pageNumberType,
          },
        });
      }
    },
  });

  dropPageNumber(pageRef);

  const [, drag, preview] = useDrag({
    item: { id: uuid, name: '', type:'replaceFlow', element:pageId },
    canDrag: isDndFlow,
    begin: () => {
      const flowId = methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][uuid];
      const template = getLatestArtboard()[CONTEXT_KEYS.SELECTED_TEMPLATE_PAGES_IN_SIDEBAR_DND];
      const generatedObj = template?.flows?.find(flow => flow.flowId === flowId);
      console.log("generatedObj :: ", generatedObj, flowId)
      return ({
        type: 'replaceFlow',
        id: uuid,
        element: generatedObj,
        boundaries: pageRef.current?.getBoundingClientRect(),
        template
      });
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  isDndFlow && drag(pageRef);

  const resizeCallback = useCallback((entry) => {
    if (!entry || !rulerEnabled || pageOverflowEnabled) return;
    const contentSize = entry.contentBoxSize[0];
    setRulerHeight(contentSize.blockSize); // TODO: Check if scaling / zoom affects the height of the page
    if (setParentHeight) {
      setParentHeight(contentSize.blockSize);
    }
  }, [rulerEnabled, pageOverflowEnabled]);

  const headerElementStyles = useCallback((e) => {
    if (getElementStyles) {
      getElementStyles(e, headerUUID, true);
    }
  }, [getElementStyles, headerUUID]);

  const blockElementStyles = useCallback((e, blockId) => {
    if (getElementStyles) {
      getElementStyles(e, blockId, false);
    }
  }, [getElementStyles]);

  const footerElementStyles = useCallback((e) => {
    if (getElementStyles) {
      getElementStyles(e, footerUUID, true)
    }
  }, [getElementStyles, footerUUID]);

  const [] = useStore(`${uuid}`, resizeCallback, `complexpage`);
  const currentEditor = editingBlockOrElement?.current?.getEditor()?.root;

  const pageWrapperRef = useRef(undefined);
  const ScrollingComponent = useMemo(() => withScrolling(PagesWrapper, pageWrapperRef), []);


  const onDoubleClick = useCallback(() => {
    updateArtboard({ [CONTEXT_KEYS.FLOW_EDIT_MODE]: uuid });
  }, []);

  const rulerAdjusterSelector = useCallback(store => store[CONTEXT_KEYS.RULER_WIDTH_ADJUSTER], []);
  const [isRulerWidthAdjuster] = useArtboardContext(rulerAdjusterSelector, `DesignPage-${uuid}`);

  const previewModalSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_MODAL], []);
  const [isPreviewModal] = useArtboardContext(previewModalSelector, `DesignPage-${uuid}`);

  const isHybridMailSelector = useCallback(store => store[CONTEXT_KEYS.IS_HYBRID_MAIL], []);
  const [isHybridDesign] = useArtboardContext(isHybridMailSelector, `DesignPage-${uuid}`);


  return (
    <>
      <div
        id={`dim-bg-${pageId}`}
        style={{ position: 'fixed',  display: blockEditing ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.1)', top:0, left: 0, height: '100%', width: '100%' }}
        onClick={disableEditing}
      />
      <DragPreviewImage connect={preview} src={ReusableDocumentFlowPreview} />
      {rulerEnabled && (
        <RulerContainer
          style={{
            width: `${scaling * width + 30}px`,
            height: `${scaling * rulerHeight + 30}px`, // TODO : Resize according to page height change
            backgroundColor: blockEditing ? '#d9d9d9' : '#ffffff'
          }}
        >
          <RulerIcon>
            <Icon component={RulerSvg} style={{ color: '#ddd' }} />
          </RulerIcon>
          <Tooltip
            placement="right"
            title={`w: ${widthInMM}`}
            defaultVisible={false}
            visible={false}
            getPopupContainer={() => document.body}
          >
            <Ruler
              rulerType="horizontal"
              ppi={96}
              rulerWidth={width || 794}
              scaling={scaling || 1}
              backgroundColor={'transparent'}
            />
          </Tooltip>
          <Tooltip
            placement="left"
            title={`h: ${rulerHeight * 25.4}`}
            defaultVisible={false}
            visible={false}
            arrowPointAtCenter
          >
            <Ruler
              rulerType="vertical"
              ppi={96}
              rulerHeight={rulerHeight}
              scaling={scaling || 1}
              backgroundColor={'transparent'}
            />
          </Tooltip>
        </RulerContainer>
      )}
      <ContextMenuContainer pageId={uuid}/>
      {blockEditing && currentEditor && (
        <VariableMenuContainer
          pageRef={pageRef}
          currentEditor={currentEditor}
          blockEditing={blockEditing}
          currentBlock={currentBlock}
          headerUUID={headerUUID}
          footerUUID={footerUUID}
        />
      )}
      <PageContent
        className={`page-content ${pageOverflowEnabled ? 'no-space-on-top' : ''}`}
        id={uuid}
        data-pageid={uuid}
        data-pagenum={flowNumber}
        ref={pageRef}
        width={width}
        height={height}
        backgroundColor={backgroundColor}
        isOver={isOver}
        scaling={scaling}
        rulerEnabled={isRulerWidthAdjuster || pageOverflowEnabled}
        previewModal={isPreviewModal}
        onDoubleClick={isStandalone ? onDoubleClick : dummyCallback}
        editable={editable}
      >
        {(pageNumber && docPageNumber) && 
          <PageNumberRenderer 
            docPageNumber={docPageNumber}
            element={pageNumber}
            pageRef={pageRef}
            methods={methods}
            isPreview={pageOverflowEnabled}
            pageId={pageId}
            parentUUID={uuid}
            editable={editable}
          />
        }
        {isHybridDesign &&
          <AddressWindow pageId={uuid} />
        }
        {headerUUID && (
          <PageStaticElement
            uuid={uuid}
            blockUUID={headerUUID}
            pageNumberUUID={pageNumberUUID}
            pageNumber={pageNumber && pageNumber.isHeader ? pageNumber : undefined}
            elementType={'header'}
            editable={editable}
            pageRef={pageRef}
            pageId={pageId}
            customClassName={'vertical-layout'}
            scaling={scaling}
            getElementStyles={headerElementStyles}
            isStandaloneFlow={isStandalone}
          />
        )}
        <ScrollingComponent id={COMPLEX_DOCUMENT_PAGE_WRAPPER} style={{ overflow: blockEditing ? 'visible' : 'auto' }}>
          <BlockLayout
            uuid={uuid}
            pageId={pageId}
            pageIndex={flowNumber}
            editable={editable && !isTocFlow}
            useStore={useStore}
            customClassName={'vertical-layout'}
            scaling={scaling}
            getElementStyles={blockElementStyles}
            isPageVisible={isPageVisible}
            isStandaloneFlow={isStandalone}
            isTocFlow={isTocFlow}
          />
        </ScrollingComponent>
        {footerUUID && (
          <PageStaticElement
            uuid={uuid}
            blockUUID={footerUUID}
            pageNumberUUID={pageNumberUUID}
            pageNumber={pageNumber && !pageNumber.isHeader ? pageNumber : undefined}
            elementType={'footer'}
            editable={editable}
            pageRef={pageRef}
            pageId={pageId}
            customClassName={'vertical-layout'}
            scaling={scaling}
            getElementStyles={footerElementStyles}
            isStandaloneFlow={isStandalone}
          />
        )}
        <div
          id={`dim-bg-${pageId}`}
          style={{ position: 'fixed', zIndex: 1, backgroundColor: 'rgba(0, 0, 0, 0.1)', top:0, left: 0, height: '100%', width: '100%', display: blockEditing ? 'block' : 'none' }}
          onClick={disableEditing}
        />
      </PageContent>
      {editable && <CustomTooltip title={t(COMPLEX_PAGE_OPTIONS.DOUBLE_CLICK_TO_EDIT_TOOLTIP_TEXT.key, COMPLEX_PAGE_OPTIONS.DOUBLE_CLICK_TO_EDIT_TOOLTIP_TEXT.value)} customized />}
      <ToolTipPopup
        id={'variableTooltipPopUp'}
        style={{ position: 'fixed',
          display:'flex',
          zIndex:'40',
          alignItems:'center',
          justifyContent:'center',
          background:'black',
          color:'white',
          borderRadius:'4px',
          minWidth:'fit-content',
          padding:'4px 8px',

          boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px'}}>

      </ToolTipPopup>
    </>
  )
}

const DesignPageWithTranslation = React.memo(withTranslation(NAMESPACES)(DesignPage));

const ComplexPage = ({ t, uuid, flowNumber, totalPages, totalPagesWithInserts, isVisible, visibilityRef, scaling }) => {

  const [tocLoad, setTocLoad] = useState(false);
  const customSelector = useCallback((store) => store[uuid], []);
  const [pageDetails, methods] = useDocumentContext(customSelector, `ComplexPage-${uuid}`);

  const isTocFlow =  pageDetails?.flowName === TABLE_OF_CONTENTS;

  const getflowId = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][uuid], [uuid]);
  const [flowUUid] = useDocumentContext(getflowId, `ComplexPage-${uuid}`);

  const [childHeight, setHeight] = useState(pageDetails?.height);
  const [hideBlockSettings, setHideBlockSettings] = useState(false);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === uuid && store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] !== true, []);
  const [blockEditing, updateArtboard,,,getLatestArtboard] = useArtboardContext(customArtboardSelector, `ComplexPage-${uuid}`);

  const pageSettingsSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === uuid && store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] === true, []);
  const [pageSettingsVisible] = useArtboardContext(pageSettingsSelector, `ComplexPage-${uuid}`);

  const currentPageEditing = useRef(blockEditing);
  const [elementStylesCallback, setElementStylesCallback] = useState(null);

  const pageOverflowSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(pageOverflowSelector, `ComplexPage-${uuid}`);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, `ComplexPage-${uuid}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexPage-${uuid}`);

  const isOpenDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `ComplexPage-${uuid}`);

  const rulerSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_RULER], []);
  const [rulerEnabled] = useArtboardContext(rulerSelector, `ComplexPage-${uuid}`);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign);

  const standaloneSelector = useCallback(store => (store[uuid].isStandalone && CurrentDesign === 'document'), [CurrentDesign]);
  const [isStandalone] = useDocumentContext(standaloneSelector, `ComplexPage-${uuid}`);

  const flowEditModeSelector = useCallback(store => store[CONTEXT_KEYS.FLOW_EDIT_MODE], []);
  const [flowEditMode] = useArtboardContext(flowEditModeSelector, `ComplexPage-${uuid}`);

  const currentBlockTypeSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK_TYPE], []);
  const [currentBlockType] = useArtboardContext(currentBlockTypeSelector);

  const { Provider, useStore } = useMemo(createResizeObserver, [pageOverflowEnabled]);
  const ObserverForIntersection = useMemo(() => {
    return createIntersectionObserver(visibilityRef?.current);
  }, []);

  const [actualScaling, setActualScaling] = useState(scaling === 'fit' ? Number.parseFloat((visibilityRef.getBoundingClientRect().height - 200) / pageDetails.height).toFixed(2) : scaling);

  const rulerAdjusterSelector = useCallback(store => store[CONTEXT_KEYS.RULER_WIDTH_ADJUSTER], []);
  const [isRulerWidthAdjuster] = useArtboardContext(rulerAdjusterSelector, `DesignPage-${uuid}`);

  const dndFlowSelector = useCallback(store => store[CONTEXT_KEYS.DND_FLOW], []);
  const [isDndFlow] = useArtboardContext(dndFlowSelector, `DesignPage-${uuid}`);

  const docToMailSelector = useCallback(store => store[CONTEXT_KEYS.IS_DOC_TO_MAIL], []);
  const [isDocToMail] = useArtboardContext(docToMailSelector, `DesignPage-${uuid}`);

  const getSelectedFlowsSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.SELECTED_FLOWS], []);
  const [selectedFlowUUIDS] = useDocumentContext(getSelectedFlowsSelector, `ComplexPage-${uuid}`);

  const previewModalSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_MODAL], []);
  const [isPreviewModal] = useArtboardContext(previewModalSelector, `DesignPage-${uuid}`);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [defaultStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const gVariablesSelector = useCallback( store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(gVariablesSelector);

  const flowBlMapSelector = useCallback( store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC], []);
  const [flowBlMap] = useDocumentContext(flowBlMapSelector);

  const { showVersionSettings } = useShowVersionSettings(`DesignPage-${uuid}`);

  useEffect(() => {
    setActualScaling(scaling === 'fit' ? Number.parseFloat((visibilityRef.getBoundingClientRect().height - 200) / pageDetails.height).toFixed(2) : scaling)
  }, [scaling])

  useEffect(() => {
    currentPageEditing.current = blockEditing; // Hoping that setting this first preceeding the next use effect would help
  }, [blockEditing]);

  useEffect(() => {
    if (!isVisible && currentPageEditing.current) { // TODO : Should the scroll be disabled on opening page settings
      // Use effect was getting called twice with combinations of values, erasing the newly set current page
      updateArtboard({
        [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
        [CONTEXT_KEYS.CURRENT_BLOCK]: '',
        [CONTEXT_KEYS.CURRENT_PAGE]: '',
        [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false,
      });
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      updateArtboard({ [CONTEXT_KEYS.CURRENT_PAGE]: '', [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false });
      // setTimeout(() => methods.setFlowSettings('dummy', { uuid }), 100);
    }
  }, []);

  const addFlow = useCallback(() => {
    const newFlowUUID = methods.addNewFlow(uuid);
    getElementBySelectorAsync(`#scroll-${newFlowUUID}-0`).then((target) => {
      scrollPageTo(target, COMPLEX_DOCUMENT_PAGE_WRAPPER);
      /*updateArtboard({ [CONTEXT_KEYS.CURRENT_PAGE]: newFlowUUID, [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: true });
      setTimeout(() => methods.setFlowSettings('dummy', { uuid }), 100);*/
    });
  }, [uuid]);

  const editLayout =useCallback(() => {
    if(flowUUid){
      window.open(`/flow/${flowUUid}`)
    }
  }, [flowUUid]);

  const deleteFlow = useCallback(() => {
    methods.deleteFlow(uuid);
  }, [uuid]);

  const openPageSettings = useCallback(() => {
    updateArtboard({ [CONTEXT_KEYS.CURRENT_PAGE]: uuid, [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: true, [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false});
  }, [uuid]);

  const swapPageUp = useCallback((e) => {
    const currentPosition = parseInt(e.currentTarget.id, 10);
    methods.swapFlows(uuid, { currentPosition, targetPosition: currentPosition - 1 });
  }, []);

  const swapPageDown = useCallback((e) => {
    const currentPosition = parseInt(e.currentTarget.id, 10);
    methods.swapFlows(uuid, { currentPosition, targetPosition: currentPosition + 1 });
  }, []);

  const showHideBlockSettings = useCallback((e => {
    let scrollElm = document.getElementById("complex-page-wrapper");
    let isForceHide =  e.target.nodeName.toLowerCase() === 'canvas' || (scrollElm?.scrollTop === 0 && e?.clientY < 155);
    if (isForceHide) {
      setHideBlockSettings(true);
    } else {
      let targetElm = e.target;
      if (targetElm) {
        if (
          targetElm.id?.includes('complex-page') ||
          targetElm.getAttribute('class')?.includes('droppable empty') ||
          targetElm.getAttribute('class')?.includes('vertical-layout')
        ) {
          setHideBlockSettings(true);
        } else {
          setHideBlockSettings(false);
        }
      } else {
        setHideBlockSettings(false);
      }
    }
  }), []);

  const onMouseLeave = useCallback(() => {     
    updateArtboard({ [CONTEXT_KEYS.FLOW_EDIT_MODE]: '' });
  }, []);

  const createNewAsset = useCallback(() => { 
    methods.createNewFlow(uuid);
    updateArtboard({ [CONTEXT_KEYS.REUSABLE_ASSET_MODAL]: true })
  }, []);

  const renderToolTip = (visible) => {  
    return <Tooltip placement="bottom" defaultVisible={true} title={t(COMPLEX_PAGE_OPTIONS.EDIT_LAYOUT_TOOLTIP.key, COMPLEX_PAGE_OPTIONS.EDIT_LAYOUT_TOOLTIP.value)}  >
      <Icon type="edit" />
    </Tooltip>
  };

  const rulerWidthAdjuster = pageOverflowEnabled || isDndFlow || isPreviewModal || isDocToMail ? 0 : 30;

  const getWidthHeight = (value, orientation, size, dimension) => {
    const  {updatedScaling, widthHeightValue} = getWidthHeightScale({value, orientation, size, dimension, isDndFlow, isDocToMail, isPreviewModal, actualScaling, scaling, rulerWidthAdjuster});
    if (actualScaling !== updatedScaling) {
      setActualScaling(updatedScaling);
    }
    return widthHeightValue;
  };

  const previewModal = () => {
    const template = getLatestArtboard()[CONTEXT_KEYS.SELECTED_TEMPLATE_PAGES_IN_SIDEBAR_DND];
    const flowData = template?.flows?.find(flow => flow.flowId === flowUUid);
    updateArtboard({
      [CONTEXT_KEYS.PREVIEW_MODAL_DETAILS]: {
        visible: true,
        type: 'flow', flowId: flowUUid,
        flowData,
        isStandalone: pageDetails.isStandalone
      }
    })
  }

  const reloadToc = useCallback(async()=> {
    try{
      setTocLoad(true);
      const documentId = methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      const flowName = methods.get()[uuid]?.flowName;
      let tocTitleBlockUUID = null;
      if (flowName === TABLE_OF_CONTENTS) {
        const blockUUID = methods.get()[uuid]?.[`${uuid}-0`][0];
        tocTitleBlockUUID = blockUUID;
      }
      const response = await getTocFlow(documentId);
      if(response.statusCode === 3120 && response?.data){
        setTocLoad(false);
        response.data.blocks[0].uuid = tocTitleBlockUUID;
        methods.replaceFlow({ flow: response.data, uuid, pageId:`${uuid}-0` }, false);
      }else {
        setTocLoad(false);
        message.error(t(COMPLEX_PAGE_OPTIONS.TOC_RELOAD_ERROR.key, COMPLEX_PAGE_OPTIONS.TOC_RELOAD_ERROR.value));
      }
    } catch(err){
      setTocLoad(false);
      message.error(t(COMPLEX_PAGE_OPTIONS.TOC_RELOAD_ERROR.key, COMPLEX_PAGE_OPTIONS.TOC_RELOAD_ERROR.value));
    }
  }, [uuid])

  const duplicatePage = useCallback(() => {
    const newFlowUUID = methods.duplicateFlow(uuid);
    getElementBySelectorAsync(`#scroll-${newFlowUUID}-0`).then((target) => {
      scrollPageTo(target, COMPLEX_DOCUMENT_PAGE_WRAPPER);
     });
  }, [uuid]);

  const { isBLPresent: flowHasBl, blId } =
    flowBlMap instanceof Map ? (flowBlMap?.has(flowUUid) ? flowBlMap.get(flowUUid) : {}) : {};

  const toggleBusinessLogicModal = useCallback(()=> {
    let highlightTimeout;
    let resetTimeout;
    if(flowHasBl) {
      updateArtboard({[CONTEXT_KEYS.TOGGLE_TAB_MENU]: {isOpen: true, openTabIndex : 13, details:{ type:'flow', flowId:flowUUid, blId: blId }}});
      if (highlightTimeout) clearTimeout(highlightTimeout);
      if (resetTimeout) clearTimeout(resetTimeout);
      highlightTimeout = setTimeout(() => {
        const el = document.getElementById(`businessLogic-${blId}`);
        if (!el) return;
    
        const scrollContainer = el.closest(".scroll-container") || el.parentElement?.parentElement?.parentElement?.parentElement;
        if (scrollContainer) {
          scrollContainer.scroll({
            top: el.offsetTop,
            behavior: "smooth",
          });
        }
    
        const parent = el.parentElement;
        if (!parent) return;
    
        // highlight target, dim siblings
        Array.from(parent.children).forEach((child) => {
          child.style.transition = "opacity 0.5s ease";
          child.style.opacity = child === el ? "1" : "0.5";
        });
    
        // Reset highlight after 2.5s
        resetTimeout = setTimeout(() => {
          Array.from(parent.children).forEach((child) => {
            child.style.transition = "opacity 0.5s ease";
            child.style.opacity = "1";
          });
        }, 2500);
      }, 1000);
    }
    
    if(!flowHasBl) updateArtboard({[CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]: { logicType: 'flow', flowUUID:uuid, }});
  },[uuid, flowHasBl, blId]);

  const selectedFlows = useCallback((check, uuid) => {
    let updatedSelectedFlows=selectedFlowUUIDS;
    if(check){
      updatedSelectedFlows.push(uuid);
    } else {
      updatedSelectedFlows= updatedSelectedFlows.filter(item => item !== uuid);
    } 
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.SELECTED_FLOWS]: updatedSelectedFlows});
  },[selectedFlowUUIDS]);

 return (
    <Provider>
      {pageDetails?.pages?.map(pageId => (
        <EditorWrapper
          id={`scroll-${pageId}`}
          key={`scroll-${pageId}`}   
          rulerEnabled={isRulerWidthAdjuster} 
          dndFlow= {isDndFlow}     
        >
          <EditingContainer
            transparent={false} // TODO : Enable for versions preview
            rulerEnabled={false} // TODO : integrate with ruler
            style={{
              maxWidth: getWidthHeight(pageDetails?.width, pageDetails.orientation, pageDetails.size, 'width'),
              height: getWidthHeight(childHeight, pageDetails.orientation, pageDetails.size, 'height'),
            }}
            outline={!editable}
            id={`editingContainer-${pageId}`}
            hideBlockSettings={hideBlockSettings}
            onMouseMove={(e) => (editable || showVersionSettings) && !blockEditing && showHideBlockSettings(e)}
            onMouseLeave={isStandalone ? onMouseLeave : dummyCallback}
          >
            {(editable || showVersionSettings) &&  !isStandalone && !isTocFlow && <BlockLayoutSettings
              pageId={pageId}
              parentUUID={uuid}
              customRef={setElementStylesCallback}
            />}
           {editable && !isInteractiveDesign && CurrentDesign === 'document' && <ComplexNewPage uuid={uuid} flowNumber={flowNumber} CurrentDesign={CurrentDesign} t={t} /> }
           {!pageOverflowEnabled && pageId === `${uuid}-0` &&
              <PageMetaDetails rulerEnabled={rulerEnabled} rulerAdjusted={isRulerWidthAdjuster} dndFlow={isDndFlow} currentDesign={CurrentDesign}>
                {CurrentDesign === 'document'  &&
                  <PageNumber style={{ fontSize: `${actualScaling < 0.3 ? Math.max(actualScaling * 16, 8) : 16}px` }}>
                    {t(COMPLEX_PAGE_OPTIONS.PAGE_HEADER_TEXT.key, COMPLEX_PAGE_OPTIONS.PAGE_HEADER_TEXT.value, { index: flowNumber + 1, totalPages: isPreviewModal ? totalPagesWithInserts : totalPages })}
                  </PageNumber>
                }
                {isDndFlow && (
                  <PageActions>
                    <PageActionItem id={flowNumber} dndFlow={isDndFlow}>
                      <Tooltip
                        title={
                          t(
                            `DESIGN_AREA_TEMPLATES_TAB:${DESIGN_AREA_TEMPLATES_TAB.TEMPLATE_PREVIEW_TOOLTIP.key}`,
                            DESIGN_AREA_TEMPLATES_TAB.TEMPLATE_PREVIEW_TOOLTIP.value,
                          ) || 'Preview'
                        }
                        placement="top"
                      >
                        <div className="preview-button" onClick={(e) => previewModal(e, uuid)}>
                          <Icon type="eye" />
                        </div>
                      </Tooltip>
                    </PageActionItem>
                  </PageActions>
                )}
                {
                  isDocToMail && pageDetails.flowName !== TABLE_OF_CONTENTS && (
                    <PageActionItem id={flowNumber}>
                     <Checkbox onChange={(e)=>selectedFlows(e.target.checked, uuid)} checked={selectedFlowUUIDS.includes(uuid)}></Checkbox>
                    </PageActionItem>
                  )
                }
                {(editable || showVersionSettings) && <PageActions>
                  {CurrentDesign === 'document' && !isInteractiveDesign && !showVersionSettings && 
                    <>
                      {editable && flowNumber !== 0 && (
                        <PageActionItem id={flowNumber} onClick={swapPageUp}>
                          <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.SWAP_WITH_PREVIOUS_PAGE.key, COMPLEX_PAGE_OPTIONS.SWAP_WITH_PREVIOUS_PAGE.value)}>
                            <Icon type="up" />
                          </Tooltip>
                        </PageActionItem>
                      )}
                      { editable && flowNumber !== totalPages - 1 && (
                        <PageActionItem id={flowNumber} onClick={swapPageDown}>
                          <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.SWAP_WITH_NEXT_PAGE.key, COMPLEX_PAGE_OPTIONS.SWAP_WITH_NEXT_PAGE.value)}>
                            <Icon type="down" />
                          </Tooltip>
                        </PageActionItem>
                      )}
                    </>
                  }
                  {CurrentDesign !== 'block' && (editable || showVersionSettings) && !isStandalone && 
                    <PageActionItem onClick={openPageSettings}>
                      <Tooltip placement="bottom" title= {`${CurrentDesign === 'block'  ? t(COMPLEX_PAGE_OPTIONS.BLOCK_SETTING_BUTTON.key, COMPLEX_PAGE_OPTIONS.BLOCK_SETTING_BUTTON.value) : t(COMPLEX_PAGE_OPTIONS.PAGE_SETTING_BUTTON.key, COMPLEX_PAGE_OPTIONS.PAGE_SETTING_BUTTON.value)}`}>
                        <Icon type="setting" />
                      </Tooltip>
                    </PageActionItem>
                  }

                  {editable && !isInteractiveDesign && (
                    <>
                      {totalPages > 1 && (
                         <PageActionItem>
                           <Tooltip
                             placement="bottom"
                             title={t(
                               COMPLEX_PAGE_OPTIONS.DELETE_PAGE_BUTTON.key,
                               COMPLEX_PAGE_OPTIONS.DELETE_PAGE_BUTTON.value,
                             )}
                           >
                             <Popconfirm
                               placement="bottom"
                               trigger="click"
                               onConfirm={() => {
                                 deleteFlow();
                               }}
                               title={t(
                                 COMPLEX_PAGE_OPTIONS.DELETE_PAGE_OVERLAY_MESSAGE.key,
                                 COMPLEX_PAGE_OPTIONS.DELETE_PAGE_OVERLAY_MESSAGE.value,
                               )}
                               okText={t(
                                 COMPLEX_PAGE_OPTIONS.YES_LABEL.key,
                                 COMPLEX_PAGE_OPTIONS.YES_LABEL.value,
                               )}
                               cancelText={t(
                                 COMPLEX_PAGE_OPTIONS.NO_LABEL.key,
                                 COMPLEX_PAGE_OPTIONS.NO_LABEL.value,
                               )}
                               overlayStyle={{ width: 350 }}
                             >
                               <Icon type="delete" />
                             </Popconfirm>
                           </Tooltip>
                         </PageActionItem>
                       )}
                      {CurrentDesign === 'document' &&
                        <PageActionItem onClick={addFlow}> {/* TODO : get orientation and size of new page */}
                          <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.ADD_NEW_PAGE_BUTTON.key, COMPLEX_PAGE_OPTIONS.ADD_NEW_PAGE_BUTTON.value)}>
                            <Icon type="plus" />
                          </Tooltip>
                        </PageActionItem>
                      }

                    </>
                  )}

                  {!showVersionSettings && !isInteractiveDesign && isStandalone &&
                    <>
                      {flowEditMode === uuid ? <PageActionItem onClick={editLayout} refrencedFlow={flowEditMode === uuid}>                    
                      <Tooltip />
                      {renderToolTip(true)}
                      </PageActionItem> :
                        <PageActionItem onClick={editLayout} >
                          <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.EDIT_LAYOUT_TOOLTIP.key, COMPLEX_PAGE_OPTIONS.EDIT_LAYOUT_TOOLTIP.value)} >
                            <Icon type="edit" />
                          </Tooltip>                          
                        </PageActionItem>}
                    </>                    
                  }
                  {!showVersionSettings && !isInteractiveDesign && CurrentDesign === DESIGN_TYPE.DOCUMENT && pageDetails.flowName !== TABLE_OF_CONTENTS &&
                    <PageActionItem onClick={duplicatePage}>
                      <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.DUPLICATE_THIS_PAGE_TOOLTIP.key, COMPLEX_PAGE_OPTIONS.DUPLICATE_THIS_PAGE_TOOLTIP.value)}>
                        <Icon type="copy" />
                      </Tooltip>
                    </PageActionItem>
                  }   
                  {!showVersionSettings && !isInteractiveDesign && !isOpenDesign && !isStandalone && CurrentDesign === 'document' && pageDetails.flowName !== TABLE_OF_CONTENTS &&
                    <PageActionItem onClick={createNewAsset}>
                      <Tooltip placement="bottom"  title={t(COMPLEX_PAGE_OPTIONS.MAKE_REUSABLE_PAGE.key, COMPLEX_PAGE_OPTIONS.MAKE_REUSABLE_PAGE.value)} >
                        {ReuseFlowIcon()}
                      </Tooltip>
                    </PageActionItem>
                  }
                  {CurrentDesign !== 'document' || isInteractiveDesign || isOpenDesign || showVersionSettings
                    ? (
                     <></>
                   ) : (
                     <PageActionItem onClick={toggleBusinessLogicModal}>
                       <Tooltip
                         placement="bottom"
                         title={
                           flowHasBl
                             ? t(
                                 COMPLEX_PAGE_OPTIONS.VIEW_BUSINESS_LOGIC_LABEL.key,
                                 COMPLEX_PAGE_OPTIONS.VIEW_BUSINESS_LOGIC_LABEL.value,
                               )
                             : t(
                                 COMPLEX_PAGE_OPTIONS.ADD_BUSINESS_LOGIC_LABEL.key,
                                 COMPLEX_PAGE_OPTIONS.ADD_BUSINESS_LOGIC_LABEL.value,
                               )
                         }
                       >
                         <Icon
                           component={() => (
                             <BusinessLogicSvg fill={flowHasBl ? 'green' : 'black'} />
                           )}
                         />
                       </Tooltip>
                     </PageActionItem>
                   )}
                  {!isInteractiveDesign && CurrentDesign === 'document' && editable && pageDetails.flowName === TABLE_OF_CONTENTS && (
                    <PageActionItem id={flowNumber} onClick= {reloadToc}>
                      <Tooltip placement="bottom" title={t(COMPLEX_PAGE_OPTIONS.TOC_RELOAD.key, COMPLEX_PAGE_OPTIONS.TOC_RELOAD.value)}>
                        <Icon type="sync" spin={tocLoad}/>
                      </Tooltip>
                    </PageActionItem>
                  )}
                </PageActions>}
              </PageMetaDetails>
            }
            {(!isStandalone && !isTocFlow) && <DesignPageWithTranslation
              key={pageId}
              uuid={uuid}
              pageId={pageId}
              scaling={actualScaling}
              rulerEnabled={rulerEnabled}
              pageOverflowEnabled={pageOverflowEnabled}
              flowNumber={flowNumber}
              widthInMM={pageDetails?.widthInMM}
              heightInMM={pageDetails?.heightInMM}
              width={pageDetails?.width}
              height={pageDetails?.height}
              backgroundColor={pageDetails?.backgroundColor}
              headerUUID={pageDetails ? pageDetails[HEADER] : false}
              footerUUID={pageDetails ? pageDetails[FOOTER] : false}
              pageNumberUUID={pageDetails ? pageDetails['pageNumber'] : false}
              useStore={useStore}
              setParentHeight={setHeight}
              getElementStyles={elementStylesCallback}
              editable={editable && !isStandalone}
              CurrentDesign={CurrentDesign}
              isStandalone={isStandalone}
              observer={ObserverForIntersection}
              isTocFlow={isTocFlow}
              isDocToMail={isDocToMail}
            />}
            {(isStandalone || isTocFlow) && 
              <ComplexDefaultStyleProvider customState={{fontSize: pageDetails?.fontSize || defaultStyle.fontSize , fontFamily: pageDetails?.fontFamily || defaultStyle.fontFamily}}>
                <DesignPageWithTranslation
                  key={pageId}
                  uuid={uuid}
                  pageId={pageId}
                  scaling={actualScaling}
                  rulerEnabled={rulerEnabled}
                  pageOverflowEnabled={pageOverflowEnabled}
                  flowNumber={flowNumber}
                  widthInMM={pageDetails?.widthInMM}
                  heightInMM={pageDetails?.heightInMM}
                  width={pageDetails?.width}
                  height={pageDetails?.height}
                  backgroundColor={pageDetails?.backgroundColor}
                  headerUUID={pageDetails ? pageDetails[HEADER] : false}
                  footerUUID={pageDetails ? pageDetails[FOOTER] : false}
                  pageNumberUUID={pageDetails ? pageDetails['pageNumber'] : false}
                  useStore={useStore}
                  setParentHeight={setHeight}
                  getElementStyles={elementStylesCallback}
                  editable={editable && !isStandalone}
                  CurrentDesign={CurrentDesign}
                  isStandalone={isStandalone}
                  observer={ObserverForIntersection}
                  isTocFlow={isTocFlow}
                  isDocToMail={isDocToMail}
                />
              </ComplexDefaultStyleProvider>
            }
          </EditingContainer>
        </EditorWrapper>
      ))}
     {!isInteractiveDesign && totalPages === flowNumber + 1 && CurrentDesign === 'document' &&
       <div style={{ margin: '8px 0px' }}>
         <ComplexNewPage
           maxWidth={actualScaling * pageDetails?.width + 30}
           uuid={uuid}
           flowNumber={flowNumber + 1}
           CurrentDesign={CurrentDesign}
           t={t} />
       </div>}
     {isVisible && <CustomDragLayer scaling={actualScaling} applyTransparency={currentBlockType === STATIC_TABLE_BLOCK} />}
    </Provider>
  );
}

export default React.memo(withTranslation(['COMPLEX_PAGE_OPTIONS', 'DESIGN_AREA_TEMPLATES_TAB'])(ComplexPage));